import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './UserManagement.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5003/api';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const { isAdmin, logout } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user',
    max_bids: 5
  });

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/');
      return;
    }
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Error al cargar usuarios');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (editingUser) {
        // Cambiar contraseña
        if (formData.password) {
          await axios.put(
            `${API_URL}/users/${editingUser.id}/password`,
            { password: formData.password },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        
        // Cambiar rol
        if (formData.role !== editingUser.role) {
          await axios.put(
            `${API_URL}/users/${editingUser.id}/role`,
            { role: formData.role },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        
        // Cambiar max_bids
        if (formData.max_bids !== editingUser.max_bids) {
          await axios.put(
            `${API_URL}/users/${editingUser.id}/max-bids`,
            { max_bids: formData.max_bids },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        
        alert('Usuario actualizado exitosamente');
      } else {
        // Crear nuevo usuario
        await axios.post(
          `${API_URL}/users`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Usuario creado exitosamente');
      }
      
      resetForm();
      loadUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      alert('Error al guardar el usuario: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      role: user.role,
      max_bids: user.max_bids || 5
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este usuario?')) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_URL}/users/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Usuario eliminado exitosamente');
        loadUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error al eliminar el usuario: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  const resetForm = () => {
    setFormData({ username: '', password: '', role: 'user', max_bids: 5 });
    setEditingUser(null);
    setShowForm(false);
  };

  return (
    <div className="user-management-container">
      <header className="user-management-header">
        <h1>👥 Gestión de Usuarios</h1>
        <div className="header-actions">
          <button onClick={() => navigate('/admin')} className="btn-back">⬅️ Volver a Admin</button>
          <button onClick={() => navigate('/')} className="btn-catalog">📱 Catálogo</button>
          <button onClick={() => { logout(); navigate('/login'); }} className="btn-logout">🚪 Salir</button>
        </div>
      </header>

      <div className="user-management-content">
        <div className="toolbar">
          <button onClick={() => setShowForm(!showForm)} className="btn-add-user">
            {showForm ? '❌ Cancelar' : '➕ Agregar Usuario'}
          </button>
        </div>

        {showForm && (
          <div className="user-form-container">
            <h2>{editingUser ? '✏️ Editar Usuario' : '➕ Nuevo Usuario'}</h2>
            <form onSubmit={handleSubmit} className="user-form">
              <div className="form-group">
                <label>Nombre de Usuario *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  required
                  disabled={editingUser !== null}
                  placeholder="usuario123"
                />
                {editingUser && (
                  <small>No se puede cambiar el nombre de usuario</small>
                )}
              </div>

              <div className="form-group">
                <label>Contraseña {editingUser ? '(dejar vacío para no cambiar)' : '*'}</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required={!editingUser}
                  placeholder="••••••••"
                />
              </div>

              <div className="form-group">
                <label>Rol *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                >
                  <option value="user">👤 Usuario (Solo visualización)</option>
                  <option value="admin">👑 Administrador (Acceso completo)</option>
                </select>
              </div>

              <div className="form-group">
                <label>Límite de Pujas Simultáneas *</label>
                <select
                  value={formData.max_bids}
                  onChange={(e) => setFormData({...formData, max_bids: parseInt(e.target.value)})}
                >
                  <option value="1">1 puja</option>
                  <option value="2">2 pujas</option>
                  <option value="3">3 pujas</option>
                  <option value="4">4 pujas</option>
                  <option value="5">5 pujas</option>
                  <option value="10">10 pujas</option>
                  <option value="-1">♾️ Ilimitado</option>
                </select>
                <small>Número máximo de equipos en los que puede ofertar simultáneamente</small>
              </div>

              <div className="form-buttons">
                <button type="submit" className="btn-save" disabled={loading}>
                  {loading ? '⏳ Guardando...' : '💾 Guardar'}
                </button>
                <button type="button" onClick={resetForm} className="btn-cancel">
                  ❌ Cancelar
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="users-table-container">
          <h2>👥 Usuarios Registrados ({users.length})</h2>
          <div className="table-responsive">
            <table className="users-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Usuario</th>
                  <th>Rol</th>
                  <th>Límite Pujas</th>
                  <th>Fecha de Creación</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id}>
                    <td><strong>#{user.id}</strong></td>
                    <td>{user.username}</td>
                    <td>
                      <span className={`role-badge ${user.role}`}>
                        {user.role === 'admin' ? '👑 Admin' : '👤 Usuario'}
                      </span>
                    </td>
                    <td>
                      {user.max_bids === -1 ? '♾️ Ilimitado' : `${user.max_bids} puja(s)`}
                    </td>
                    <td>{new Date(user.created_at).toLocaleDateString('es-DO')}</td>
                    <td className="action-buttons">
                      <button onClick={() => handleEdit(user)} className="btn-edit">✏️</button>
                      {user.id !== 1 && (
                        <button onClick={() => handleDelete(user.id)} className="btn-delete">🗑️</button>
                      )}
                      {user.id === 1 && <span className="protected-badge">🔒</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
